/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

import { get, ref, type Database } from "firebase/database";

export const USERNAME_MIN_LENGTH = 3;
export const USERNAME_MAX_LENGTH = 20;

// Lowercase ASCII letters, digits, and underscore. Anchored.
export const USERNAME_REGEX = /^[a-z0-9_]+$/;

// Apple private-relay "Sign In with Apple" anonymized display names are a 10-char
// base32-ish opaque id (e.g. "a1b2c3d4e5"). We also treat the @privaterelay.appleid.com
// email domain as a signal the displayName is disposable.
export const APPLE_PRIVATE_RELAY_DISPLAY_REGEX = /^[a-z0-9]{10}$/;
export const APPLE_PRIVATE_RELAY_EMAIL_SUFFIX = "@privaterelay.appleid.com";

export const FALLBACK_DISPLAY_NAME = "MyAmbits User";

export type UsernameValidationError =
  | "empty"
  | "too-short"
  | "too-long"
  | "bad-chars";

/**
 * Validate a (already-lowercased) username candidate against length + regex
 * rules. Returns null when valid, or an error code otherwise.
 */
export function validateUsername(candidate: string): UsernameValidationError | null {
  if (!candidate) return "empty";
  if (candidate.length < USERNAME_MIN_LENGTH) return "too-short";
  if (candidate.length > USERNAME_MAX_LENGTH) return "too-long";
  if (!USERNAME_REGEX.test(candidate)) return "bad-chars";
  return null;
}

/**
 * Normalize arbitrary input toward a valid username shape: lowercase, strip
 * disallowed characters, trim to the max length. Does not guarantee the result
 * is non-empty or long enough — callers must still run `validateUsername`.
 */
export function normalizeUsernameInput(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, "")
    .slice(0, USERNAME_MAX_LENGTH);
}

/**
 * Derive up to `count` username suggestions from a display name. Produces a mix
 * of styles (base handle, suffixed variants, numeric variants) so the user can
 * pick one without typing. All results are guaranteed to satisfy
 * `validateUsername`.
 */
export function suggestUsernames(displayName: string, count = 3): string[] {
  const stripped = displayName
    .toLowerCase()
    .replace(/[^a-z0-9 _]/g, "")
    .trim();

  const parts = stripped.split(/\s+/).filter(Boolean);
  const first = parts[0] ?? "";
  const last = parts[parts.length - 1] ?? "";
  const firstInitial = first.slice(0, 1);
  const lastInitial = last.slice(0, 1);

  const candidates: string[] = [];
  const push = (s: string) => {
    const norm = normalizeUsernameInput(s);
    if (validateUsername(norm) === null && !candidates.includes(norm)) {
      candidates.push(norm);
    }
  };

  if (first && last && first !== last) {
    push(`${first}_${lastInitial}`);
    push(`${first}${lastInitial}`);
    push(`${lastInitial}${first}`);
    push(`${last}${firstInitial}`);
  }
  if (first) {
    push(first);
    push(`${first}123`);
    push(`${first}_`);
    push(`the_${first}`);
  }
  if (last && last !== first) {
    push(last);
  }

  // Pad with rand-suffixed fallbacks so we always return `count` options.
  while (candidates.length < count) {
    const base = first || "user";
    const suffix = Math.floor(Math.random() * 9000 + 1000).toString();
    push(`${base}${suffix}`);
  }

  return candidates.slice(0, count);
}

/**
 * Heuristic: true when the stored displayName looks like the generic fallback
 * or an Apple-private-relay-generated opaque id. Used to decide whether to
 * overwrite displayName after the user chooses a username.
 */
export function isPlaceholderDisplayName(
  displayName: string | null | undefined,
  email: string | null | undefined,
): boolean {
  if (!displayName) return true;
  if (displayName === FALLBACK_DISPLAY_NAME) return true;
  if (APPLE_PRIVATE_RELAY_DISPLAY_REGEX.test(displayName)) return true;
  if (email && email.toLowerCase().endsWith(APPLE_PRIVATE_RELAY_EMAIL_SUFFIX)) return true;
  return false;
}

/**
 * Look up whether a username is currently claimed. Returns `true` when free.
 * Does not validate the input — callers should `validateUsername` first.
 */
export async function isUsernameAvailable(
  database: Database,
  username: string,
): Promise<boolean> {
  const snap = await get(ref(database, `usernames/${username}`));
  return !snap.exists();
}
